import { zoneRequiresApproval } from 'features/evacZones/utils';
import { PendingReviewEvacZoneErrorType } from 'features/geoEvents/geoEvent.types';
import { TFunction } from 'i18next';
import {
  EvacZone,
  FormEvacZoneStatus,
  GeoEventEvacZoneStatus,
} from 'shared/types';
import { formatNameList } from 'shared/utils';
import * as Yup from 'yup';

type EvacZoneStatus = GeoEventEvacZoneStatus | FormEvacZoneStatus;

export const PENDING_REVIEW_ZONES_ERRORS: Record<
  string,
  PendingReviewEvacZoneErrorType
> = {
  notAccepted: 'not-accepted',
  restrictedWithStatus: 'restricted-with-status',
};

const getChangedOrRemovedPendingZones = (
  previousStatuses: GeoEventEvacZoneStatus[],
  currentStatusMap: Map<string, EvacZoneStatus>,
  pendingZoneMap: Map<string, EvacZone>,
  releasePendingZoneUids: string[],
): EvacZoneStatus[] =>
  previousStatuses.filter((prevZone) => {
    const pendingZone = pendingZoneMap.get(prevZone.evacZone.uidV2);
    if (
      !pendingZone ||
      !zoneRequiresApproval(pendingZone, releasePendingZoneUids)
    ) {
      return false;
    }

    const currentZone = currentStatusMap.get(prevZone.evacZone.uidV2);
    const isRemoved = !currentZone;
    const isChanged = currentZone && prevZone.status !== currentZone.status;

    return isRemoved || isChanged;
  });

const getAddedPendingZones = (
  previousStatuses: GeoEventEvacZoneStatus[],
  currentStatuses: EvacZoneStatus[],
  pendingZoneMap: Map<string, EvacZone>,
  releasePendingZoneUids: string[],
): EvacZoneStatus[] => {
  const previousStatusUids = new Set(
    previousStatuses.map((status) => status.evacZone.uidV2),
  );

  return currentStatuses.filter((currentZone) => {
    const { uidV2 } = currentZone.evacZone;
    const pendingZone = pendingZoneMap.get(uidV2);
    if (previousStatusUids.has(uidV2) || !pendingZone) return false;

    return zoneRequiresApproval(pendingZone, releasePendingZoneUids);
  });
};

/**
 * Identifies zones with restricted statuses after a group's pending review is accepted.
 * These are typically parent/active zones in a split/merge that are being replaced
 * and should not retain a status.
 */
const getRestrictedStatusZones = (
  currentStatuses: EvacZoneStatus[],
  pendingZoneMap: Map<string, EvacZone>,
  releasePendingZoneUids: string[],
  previousStatusMap: Map<string, GeoEventEvacZoneStatus>,
): EvacZoneStatus[] =>
  currentStatuses.filter((zone) => {
    const { uidV2 } = zone.evacZone;
    const pendingZone = pendingZoneMap.get(uidV2);
    if (!pendingZone?.isActive) return false;

    const hasReleaseBeenAccepted = !zoneRequiresApproval(
      pendingZone,
      releasePendingZoneUids,
    );
    const previousStatus = previousStatusMap.get(uidV2);

    // If status is unchanged and release isn't accepted, it's not restricted
    if (previousStatus?.status === zone.status && !hasReleaseBeenAccepted) {
      return false;
    }

    // A zone survives a split if it lists itself as both a parent and a child
    const willSurviveSplit =
      pendingZone.parentUids?.includes(uidV2) &&
      pendingZone.childUids?.includes(uidV2);

    // A rename hold keeps the zone's identity, so its status survives the release
    const isRenameHold = Boolean(pendingZone.pendingDisplayName);

    return !willSurviveSplit && !isRenameHold;
  });

export const getEvacZoneStatusesValidationSchema = (
  t: TFunction,
  regionEvacZones: EvacZone[],
  geoEventEvacZoneStatuses: GeoEventEvacZoneStatus[],
): {
  evacZoneStatuses: Yup.AnySchema;
} => {
  const previousStatusMap = new Map(
    geoEventEvacZoneStatuses.map((status) => [status.evacZone.uidV2, status]),
  );

  const pendingReviewEvacZones = regionEvacZones.filter(
    (zone) => zone.isPendingReview,
  );

  const pendingZoneMap = new Map(
    pendingReviewEvacZones.map((zone) => [zone.uidV2, zone]),
  );

  return {
    evacZoneStatuses: Yup.array().test(
      'pending-review-zones',
      '', // Placeholder message, handled by createError
      function (currentStatuses: EvacZoneStatus[] | undefined) {
        if (!currentStatuses) return true;

        const releasePendingZoneUids: string[] =
          this.parent?.releasePendingZoneUids || [];
        const currentStatusMap = new Map(
          currentStatuses.map((status) => [status.evacZone.uidV2, status]),
        );

        // Check for pending zones that were changed/added without acceptance
        const changedOrRemovedZones = getChangedOrRemovedPendingZones(
          geoEventEvacZoneStatuses,
          currentStatusMap,
          pendingZoneMap,
          releasePendingZoneUids,
        );
        const addedZones = getAddedPendingZones(
          geoEventEvacZoneStatuses,
          currentStatuses,
          pendingZoneMap,
          releasePendingZoneUids,
        );
        const unacceptedPendingZones = [
          ...changedOrRemovedZones,
          ...addedZones,
        ];

        if (unacceptedPendingZones.length) {
          return this.createError({
            message: formatNameList(
              t,
              unacceptedPendingZones.map((zone) => zone.evacZone.displayName),
            ),
            path: this.path,
            type: PENDING_REVIEW_ZONES_ERRORS.notAccepted,
          });
        }

        // Check for accepted zones that are not allowed to have a status
        const restrictedStatusZones = getRestrictedStatusZones(
          currentStatuses,
          pendingZoneMap,
          releasePendingZoneUids,
          previousStatusMap,
        );

        if (restrictedStatusZones.length) {
          return this.createError({
            message: formatNameList(
              t,
              restrictedStatusZones.map((zone) => zone.evacZone.displayName),
            ),
            path: this.path,
            type: PENDING_REVIEW_ZONES_ERRORS.restrictedWithStatus,
          });
        }

        return true;
      },
    ),
  };
};
